import { queryModelDetails } from '@/components/Model/service';
import { useModelStore } from '@/store/model';
import { toRefs } from 'vue';
import type { ModelDetailsInfo } from '@/types/model';
import { useArtworkStore } from '@/store/artwork';
import { cloneDeep, isNumber } from 'lodash';
import { useUserStore } from '@/store/user';
import router from '@/router';
import type { ModelDetailsState } from '@/types/model';
import { useTeamEditionStore } from '@/store/teamEdition';
import { message } from 'ant-design-vue';
import { ModuleEnum } from '@/assets/artwork';
import { resolveA3PerturbConfigFromDetail } from '@/assets/artwork/module/a3/resolveA3EnablePerturbDefault';
import { ModuleRoute } from '@/types/artwork/generate';
import { ModuleRouteKey } from '@xmiles/holopix-types';

// 定义在store中实际存在的模块类型
type StoreModuleRoute = Exclude<ModuleRoute, 'palette'>;

// 辅助函数：检查模块是否在store中存在
function isStoreModule(moduleRoute: ModuleRoute): moduleRoute is StoreModuleRoute {
  return moduleRoute !== 'palette';
}

// 处理模型更新生图参数默认值
export function handleFirstModelUpdate() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  for (let key in ModuleEnum) {
  
    if (Number(key)) {
      if (!artworkStore.skipResetResultModules.includes(Number(key))) {
        const moduleRoute = ModuleEnum[key] as ModuleRoute;
        
        // 检查模块是否在store中存在
        if (!isStoreModule(moduleRoute)) {
          continue;
        }

        // 各模块生图参数
        if (!artworkStore[moduleRoute]?.params) {
          continue;
        }

        // 画面增强默认：A3 吃后台 detail.enablePerturb + perturb；A1 会员默认开；其余关
        if (typeof artworkStore[moduleRoute].params.enablePerturb !== 'undefined') {
          const ckptType = artworkStore.firstModel?.ckptType;
          if (ckptType === 'A3') {
            const a3Perturb = resolveA3PerturbConfigFromDetail(artworkStore.firstModel);
            artworkStore[moduleRoute].params.enablePerturb = a3Perturb.enablePerturb;
            // enablePerturb=true 时用后台 perturb 做步数默认；关闭时也预填，便于用户再开
            artworkStore[moduleRoute].params.perturb = a3Perturb.perturb;
          } else if (userStore.isMemberUser && ckptType === 'A1') {
            artworkStore[moduleRoute].params.enablePerturb = true;
          } else {
            artworkStore[moduleRoute].params.enablePerturb = false;
          }
        }
  
        // 高清修复
        if (typeof artworkStore[moduleRoute].params.hdFix !== 'undefined') {
          if (userStore.isMemberUser && artworkStore.firstModel.ckptType === 'A1') {
            artworkStore[moduleRoute].params.hdFix = true;
          } else {
            artworkStore[moduleRoute].params.hdFix = false;
          }
        }
      }
    }
  }
};

export async function selectModel() {
  const modelStore = useModelStore();
  const { open, activeModelIndex, firshIsController } = toRefs(modelStore);

  const { data } = await queryModelDetails({ id: modelStore.activeModelId! });

  if (['edit'].includes(router.currentRoute.value.name as string) && data.ckptType === 'Flux') {
    open.value = true;
    return false;
  } else if (router.currentRoute.value.name === 'fission' && data.ckptType !== 'Flux') {
    open.value = true;
    message.warn('相似图裂变只能使用HOLO - V1模型');

    return false;
  } else if (router.currentRoute.value.name === ModuleRouteKey.Expression) {
    if (!['Flux', 'A1', 'Pony', 'Animagine'].includes(data.ckptType)) {
      open.value = true;
      message.warn('角色一致性-表情更改功能只支持【HOLO -A1】、【HOLO - V1】、【Animagine】、【Pony】系列模型使用，请选择相应的模型');
      return false;
    }
  }

  open.value = false;
  const index = activeModelIndex.value;

  const obj: ModelDetailsInfo = { ...data };
  const artworkStore = useArtworkStore();

  if (data.loraType === 'Style') {
    if (index === 0 && artworkStore.artworkState.modelList.length > 1) {
      artworkStore.artworkState.modelList = [];
    }
  } else {
    if (data.loraType === 'LoRA') {
      if (index > 0) {
        obj.strength = 0.8 - index * 0.1;
      } else {
        obj.strength = obj.loraList[0].defaultStrength;
      }
    } else {
      obj.strength = obj.loraList[0].defaultStrength;
    }
  }

  if (index === 0) {
    if (artworkStore.artworkState.modelList.length > 1) {
      const oldCkptType = artworkStore.artworkState.modelList[0]?.ckptType;

      if (data.ckptType !== oldCkptType) {
        artworkStore.artworkState.modelList = [];
      }
    }
  }

  const newModelList = cloneDeep(artworkStore.artworkState.modelList);

  if (firshIsController.value) {
    newModelList.unshift(obj);
  } else {
    newModelList[index] = obj;
  }

  await artworkStore.updateArtwork({
    modelList: newModelList
  }, {
    edit: true,
    updateModelState: false
  });

  // 选择首个模型，记录到用户属性中
  if (index === 0) {
    const userStore = useUserStore();
    const teamEditionStore = useTeamEditionStore();
    userStore.setCustom({
      key: teamEditionStore.userType === 'personal' ? 'model' : 'teamModel',
      value: data.id.toString()
    });

    // 修改参数默认值
    handleFirstModelUpdate();
  }
}


const notUpdateFields = [
  'strength',
];
export function updataModelState(oldValue: ModelDetailsState & { strength?: number | null }, newValue: ModelDetailsState) {
  for (let key in newValue) {
    if (!notUpdateFields.includes(key)) {
      (oldValue[key as keyof typeof oldValue] as unknown) = newValue[key as keyof typeof newValue];
    }
  }

  if (isNumber(newValue.maxStrength) && isNumber(newValue.minStrength) && !isNumber(oldValue.strength)) {
    const defaultStrength = newValue.loraList[0]?.defaultStrength;
    oldValue.strength = defaultStrength || (newValue.maxStrength - newValue.minStrength) / 2;
  }
}

export function updataModelListState(oldModelList: ModelDetailsState[], newModelList: ModelDetailsState[]) {
  oldModelList.forEach((item, index) => {
    updataModelState(item, newModelList[index]);
  });
}